/**
 * Schedule evaluation shared by the server (missed-run detection, previews) and mirrored exactly by the
 * Go agent (agent/internal/schedule). Both implementations are tested against testdata/schedule/vectors.json.
 *
 * Wall-clock semantics (identical to Temporal's "compatible" disambiguation):
 *  - Slots are defined in local wall-clock time of the schedule's IANA time zone.
 *  - Non-existent local times (spring-forward gap) run at the same wall time shifted forward by the gap
 *    length (02:30 during a 02:00->03:00 jump runs at 03:30).
 *  - Ambiguous local times (fall-back overlap) run once, at the EARLIER instant.
 *  - Interval schedules step in local wall-clock minutes from the window start; if two wall slots map to the
 *    same instant only one slot is produced.
 *  - A randomized start offset (jitter) is deterministic per (device, slot) so server and agent agree.
 */
const MINUTE = 60_000;
const DAY = 86_400_000;
const dtfCache = new Map();
function dtf(tz) {
    let f = dtfCache.get(tz);
    if (!f) {
        f = new Intl.DateTimeFormat('en-US', {
            timeZone: tz,
            hourCycle: 'h23',
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
        });
        dtfCache.set(tz, f);
    }
    return f;
}
export function isValidTimeZone(tz) {
    try {
        dtf(tz);
        return true;
    }
    catch {
        return false;
    }
}
export function localParts(tz, utcMs) {
    const parts = dtf(tz).formatToParts(new Date(utcMs));
    const get = (t) => Number(parts.find((p) => p.type === t)?.value ?? '0');
    const year = get('year');
    const month = get('month');
    const day = get('day');
    const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
    return { year, month, day, hour: get('hour'), minute: get('minute'), second: get('second'), weekday };
}
/** Offset (local - UTC) in ms at the given instant, at whole-second precision. */
export function tzOffsetMs(tz, utcMs) {
    const floored = Math.floor(utcMs / 1000) * 1000;
    const p = localParts(tz, floored);
    const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
    return asUtc - floored;
}
/** Convert a local wall-clock time to a UTC instant using "compatible" disambiguation. */
export function wallToUtc(tz, year, month, day, hour, minute) {
    const wall = Date.UTC(year, month - 1, day, hour, minute, 0);
    const offBefore = tzOffsetMs(tz, wall - DAY);
    const offAfter = tzOffsetMs(tz, wall + DAY);
    const candidates = [...new Set([wall - offBefore, wall - offAfter])]
        .filter((c) => c + tzOffsetMs(tz, c) === wall)
        .sort((a, b) => a - b);
    if (candidates.length > 0)
        return candidates[0];
    // Gap: interpret with the pre-transition offset, which lands after the jump shifted by the gap length.
    return wall - offBefore;
}
export function parseHHMM(s) {
    const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(s);
    if (!m)
        throw new Error(`invalid time "${s}", expected HH:MM`);
    return Number(m[1]) * 60 + Number(m[2]);
}
function addDays(y, m, d, n) {
    const t = new Date(Date.UTC(y, m - 1, d + n));
    return [t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate()];
}
/**
 * Continuous schedules produce no slots: a device on one backs up because its watched folder changed,
 * never because a moment arrived. Every slot-based helper below is therefore empty/null for them, and
 * callers that need a cadence (alerting, previews) use CONTINUOUS_DEFAULTS instead.
 */
export function isContinuous(spec) {
    return spec.kind === 'continuous';
}
export const CONTINUOUS_DEFAULTS = {
    debounceSeconds: 10,
    maxDelayMinutes: 60,
    safetyNetMinutes: 360,
};
/** Wall-clock minute offsets (from local midnight of the slot's day) produced for one local day. */
function dayMinutes(spec) {
    if (spec.kind === 'interval') {
        const every = spec.everyMinutes ?? 0;
        if (every < 5 || every > 1440)
            throw new Error('everyMinutes must be within 5..1440');
        const start = spec.windowStart ? parseHHMM(spec.windowStart) : 0;
        let end = spec.windowEnd ? parseHHMM(spec.windowEnd) : 1440;
        if (spec.windowEnd && end <= start)
            end += 1440; // crosses midnight
        const out = [];
        for (let m = start; m < end; m += every)
            out.push(m);
        return out;
    }
    const times = (spec.times ?? []).map(parseHHMM);
    if (times.length === 0)
        throw new Error('daily/weekly schedules need at least one time');
    return [...new Set(times)].sort((a, b) => a - b);
}
function dayAllowed(spec, weekday) {
    if (spec.kind === 'weekly')
        return (spec.days ?? []).includes(weekday);
    if (spec.days && spec.days.length > 0)
        return spec.days.includes(weekday);
    return true;
}
/**
 * Nominal slot instants (UTC ms, before jitter) in the half-open interval (fromMs, toMs].
 * Slot eligibility by weekday uses the local date on which the slot's day (window) starts.
 */
export function slotsBetween(spec, tz, fromMs, toMs) {
    if (isContinuous(spec))
        return [];
    if (toMs <= fromMs)
        return [];
    const minutes = dayMinutes(spec);
    const startLocal = localParts(tz, fromMs);
    // Start one day early so windows that cross midnight are covered.
    let [y, m, d] = addDays(startLocal.year, startLocal.month, startLocal.day, -1);
    const out = new Set();
    for (let guard = 0; guard < 3700; guard++) {
        const weekday = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
        const dayStartUtc = wallToUtc(tz, y, m, d, 0, 0);
        if (dayStartUtc - 2 * DAY > toMs)
            break;
        if (dayAllowed(spec, weekday)) {
            for (const min of minutes) {
                const [yy, mm, dd] = addDays(y, m, d, Math.floor(min / 1440));
                const mod = min % 1440;
                const inst = wallToUtc(tz, yy, mm, dd, Math.floor(mod / 60), mod % 60);
                if (inst > fromMs && inst <= toMs)
                    out.add(inst);
            }
        }
        [y, m, d] = addDays(y, m, d, 1);
    }
    return [...out].sort((a, b) => a - b);
}
export function nextSlot(spec, tz, afterMs) {
    if (isContinuous(spec))
        return null;
    for (let span = 2; span <= 64; span *= 2) {
        const s = slotsBetween(spec, tz, afterMs, afterMs + span * DAY);
        if (s.length > 0)
            return s[0];
    }
    return null;
}
export function previousSlot(spec, tz, atOrBeforeMs) {
    if (isContinuous(spec))
        return null;
    for (let span = 2; span <= 64; span *= 2) {
        const s = slotsBetween(spec, tz, atOrBeforeMs - span * DAY, atOrBeforeMs);
        if (s.length > 0)
            return s[s.length - 1];
    }
    return null;
}
/** 32-bit FNV-1a over UTF-8 bytes; mirrored by Go's hash/fnv New32a. */
export function fnv1a32(s) {
    const bytes = new TextEncoder().encode(s);
    let h = 0x811c9dc5;
    for (const b of bytes) {
        h ^= b;
        h = Math.imul(h, 0x01000193) >>> 0;
    }
    return h >>> 0;
}
/** Deterministic randomized start offset in ms, in [0, jitterMinutes*60s). */
export function jitterOffsetMs(deviceId, slotMs, jitterMinutes) {
    if (jitterMinutes <= 0)
        return 0;
    const seconds = jitterMinutes * 60;
    return (fnv1a32(`${deviceId}:${slotMs}`) % seconds) * 1000;
}
/**
 * The slot whose run is due at `nowMs` given the last slot already handled, applying catch-up rules:
 * returns the most recent missed slot (runs once) or null. Mirrors agent scheduler behavior.
 */
export function dueSlot(spec, tz, deviceId, lastHandledSlotMs, nowMs) {
    const prev = previousSlot(spec, tz, nowMs);
    if (prev === null)
        return null;
    if (lastHandledSlotMs !== null && prev <= lastHandledSlotMs)
        return null;
    if (prev + jitterOffsetMs(deviceId, prev, spec.jitterMinutes) > nowMs) {
        // Not yet reached after jitter; an older missed slot may still be due for catch-up.
        const older = previousSlot(spec, tz, prev - 1);
        if (older !== null && spec.catchUp === 'once' && (lastHandledSlotMs === null || older > lastHandledSlotMs))
            return older;
        return null;
    }
    if (spec.catchUp === 'none') {
        // Without catch-up only run if we are within the slot's grace (one jitter window + 15 minutes).
        const grace = spec.jitterMinutes * MINUTE + 15 * MINUTE;
        if (nowMs - prev > grace)
            return null;
    }
    return prev;
}
/** Expected cadence used for "missed backup" alerting: the longest gap between consecutive slots in a week. */
export function maxSlotGapMs(spec, tz, fromMs) {
    // A continuous device should never be quiet for long; the safety-net sweep is its worst-case cadence.
    if (isContinuous(spec))
        return (spec.safetyNetMinutes ?? CONTINUOUS_DEFAULTS.safetyNetMinutes) * MINUTE;
    const s = slotsBetween(spec, tz, fromMs, fromMs + 14 * DAY);
    let gap = 0;
    for (let i = 1; i < s.length; i++)
        gap = Math.max(gap, s[i] - s[i - 1]);
    return gap || 7 * DAY;
}
/** The next `count` run instants (UTC ms, jitter applied for `deviceId`) strictly after `fromMs`. */
export function upcomingRuns(spec, tz, count, deviceId = 'preview', fromMs = Date.now()) {
    const out = [];
    let t = fromMs;
    for (let i = 0; i < count; i++) {
        const s = nextSlot(spec, tz, t);
        if (s === null)
            break;
        out.push(s + jitterOffsetMs(deviceId, s, spec.jitterMinutes));
        t = s;
    }
    return out;
}
//# sourceMappingURL=schedule.js.map